import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, db } from '../lib/firebase';
import { doc, updateDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { User } from '../types';

interface AuthContextType {
    currentUser: User | null;
    loading: boolean;
    refreshUser: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    currentUser: null,
    loading: true,
    refreshUser: async () => { },
    logout: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    const mapUser = (u: any): User => ({
        id: u.id,
        name: u.name || u.email?.split('@')[0],
        username: u.username,
        email: u.email,
        role: u.role || 'user',
        avatarUrl: u.avatar_url || u.avatarUrl,
        balance: Number(u.balance) || 0,
        joinDate: Number(u.join_date) || u.joinDate || Date.now(),
        bio: u.bio,
        gender: u.gender,
        birthDate: u.birth_date || u.birthDate,
        totalSales: u.total_sales || u.totalSales || 0,
        followersCount: u.followers_count || u.followersCount || 0,
        favoritesCount: u.favorites_count || u.favoritesCount || 0,
        lastIp: u.last_ip || u.lastIp,
        deviceId: u.device_id || u.deviceId,
        riskScore: u.risk_score || u.riskScore || 100,
        riskStatus: u.risk_status || u.riskStatus || 'SAFE',
        riskSignals: u.risk_signals || u.riskSignals,
        lastRiskDecayAt: u.last_risk_decay_at || u.lastRiskDecayAt,
        verifiedIbans: u.verified_ibans || u.verifiedIbans || [],
        userType: u.user_type || u.userType || 'INDIVIDUAL',
        storeName: u.store_name || u.storeName || '',
        city: u.city,
        district: u.district,
        successfulTradesCount: u.successful_trades_count || u.successfulTradesCount || 0,
        avgResponseTime: u.avg_response_time || u.avgResponseTime || 'Aynı gün',
        isIdentityVerified: u.is_identity_verified || u.isIdentityVerified || false,
        lastSeen: u.last_seen || u.lastSeen || u.last_active,
        isVacationMode: u.is_vacation_mode || u.isVacationMode || false,
        preferredCarrier: u.preferred_carrier || u.preferredCarrier,
        pinnedListingIds: u.pinned_listing_ids || u.pinnedListingIds || [],
        followingIds: u.following_ids || u.followingIds || [],
        preferredRegion: u.preferredRegion || 'TR',
        preferredLanguage: u.preferredLanguage || 'tr-TR',
        preferredCurrency: u.preferredCurrency || 'TRY',
        marketRegion: u.marketRegion || u.preferredRegion || 'TR'
    });

    useEffect(() => {
        let unsubBalance: (() => void) | null = null;
        let unsubUser: (() => void) | null = null;

        const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
            setLoading(true);

            // Clean up previous listeners
            if (unsubBalance) { unsubBalance(); unsubBalance = null; }
            if (unsubUser) { unsubUser(); unsubUser = null; }

            if (firebaseUser) {
                // Tracking Logic
                let deviceId = localStorage.getItem('qs_device_id') || ('dev_' + Math.random().toString(36).substring(2, 15));
                localStorage.setItem('qs_device_id', deviceId);

                // Update basic user info (IP, last seen) once on login
                fetch('https://api.ipify.org?format=json')
                    .then(res => res.json())
                    .then(ipData => {
                        if (ipData.ip) {
                            // 🛡️ Safe update: Only update if doc exists to avoid permission errors on new accounts
                            const userRef = doc(db, 'users', firebaseUser.uid);
                            updateDoc(userRef, {
                                last_ip: ipData.ip,
                                device_id: deviceId,
                                last_seen: Date.now()
                            }).catch(e => {
                                // Ignore permission-denied for brand new users - Auth.tsx will create the doc
                                if (e.code !== 'permission-denied' && !e.message.includes('not-found')) {
                                    console.warn("Update session info deferred", e.message);
                                }
                            });
                        }
                    }).catch(e => console.warn("IP tracking skipped", e));

                const userDocRef = doc(db, 'users', firebaseUser.uid);
                const balanceDocRef = doc(db, 'balances', firebaseUser.uid);

                // Listener 1: User Profile Data (Atomic)
                unsubUser = onSnapshot(userDocRef, (snap) => {
                    if (snap.exists()) {
                        const data = snap.data();
                        const mapped = mapUser({ id: snap.id, ...data });

                        setCurrentUser(prev => {
                            if (!prev) return mapped;
                            // Healing Max: Take the highest value between sources to prevent data loss in UI
                            return {
                                ...mapped,
                                balance: Math.max(prev.balance || 0, mapped.balance || 0),
                                reservedBalance: Math.max(prev.reservedBalance || 0, data.reservedBalance || 0)
                            };
                        });
                    } else {
                        // 🧹 Self-Healing: Doc missing but user logged in -> Recreate Profile
                        // This fixes "old account" issues where data was deleted but session remains
                        console.warn("User profile missing. Attempting self-healing... 🩹");
                        const newProfile = {
                            id: firebaseUser.uid,
                            email: firebaseUser.email,
                            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Gezgin',
                            username: (firebaseUser.email?.split('@')[0] || 'user_' + Math.random().toString(36).substring(2, 7)).toLowerCase(),
                            join_date: Date.now(),
                            role: 'user',
                            balance: 0
                        };

                        // Use setDoc to create it - Rules allow this for owner with defaults
                        const heal = async () => {
                            try {
                                await setDoc(userDocRef, newProfile);
                                console.log("Self-healing successful! Profile restored. ✅");
                            } catch (e: any) {
                                // If it fails with permission denied, it might mean the role/balance check in 'update' triggered
                                // which happens if Auth.tsx created it at the exact same time.
                                if (e.code !== 'permission-denied') {
                                    console.error("Self-healing failed:", e.message);
                                }
                            }
                        };
                        heal();
                    }
                }, (error) => {
                    // Handle specific errors like permission denied gracefully
                    if (error.code === 'permission-denied') {
                        console.warn("Firestore access restricted - User doc likely missing or protected.");
                        setCurrentUser(null);
                    } else {
                        console.error("User snapshot error:", error);
                    }
                });

                // Listener 2: Financial Data (Detailed Balance Collection)
                unsubBalance = onSnapshot(balanceDocRef, (snap) => {
                    const b = snap.exists() ? snap.data() : { available: 0, pending: 0 };
                    const available = b.available ?? b.balance ?? 0;
                    const pending = b.pending ?? b.reservedBalance ?? 0;

                    setCurrentUser(prev => {
                        if (!prev) {
                            return {
                                id: firebaseUser.uid,
                                name: firebaseUser.displayName || '...',
                                email: firebaseUser.email || '',
                                username: '...',
                                role: 'user',
                                balance: available,
                                reservedBalance: pending,
                                joinDate: Date.now()
                            } as User;
                        }

                        return {
                            ...prev,
                            // Healing Max: Ensure balance doesn't drop to 0 if users doc has a higher valid value
                            balance: Math.max(prev.balance || 0, available),
                            reservedBalance: Math.max(prev.reservedBalance || 0, pending)
                        };
                    });
                });

            } else {
                setCurrentUser(null);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            if (unsubBalance) unsubBalance();
            if (unsubUser) unsubUser();
        };
    }, []);

    // Presence Heartbeat
    useEffect(() => {
        if (!currentUser?.id) return;

        const heartbeat = setInterval(() => {
            if (!auth.currentUser) return;
            const userRef = doc(db, 'users', currentUser.id);
            updateDoc(userRef, {
                lastSeen: Date.now(),
                last_seen: Date.now()
            }).catch(e => {
                // Only log if it's not a permission error (which we know is caused by App Check right now)
                if (e.code !== 'permission-denied') {
                    console.warn("Heartbeat update failed:", e.message);
                }
            });
        }, 180000); // 3 minutes

        return () => clearInterval(heartbeat);
    }, [currentUser?.id]);

    const refreshUser = async () => {
        // Handled by onSnapshot auto-reactivity
    };

    const logout = async () => {
        await auth.signOut();
        setCurrentUser(null);
    };

    return (
        <AuthContext.Provider value={{ currentUser, loading, refreshUser, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
